<template>
  <!--
    AB#22228 (Zendesk #9545). One dropdown per roster size that no longer exists on the
    product; the copy cannot be saved until every one has a current size chosen. Used by the
    Copy Order modal and by Copy Item in the tech pack editor.
  -->
  <div class="flex flex-col gap-3" data-test="resolve-obsolete-sizes">
    <div>
      <h2 class="text-lg font-semibold">Some sizes no longer exist</h2>
      <p class="text-sm text-ink-soft">
        The size list of {{ reports.length === 1 ? 'this product' : 'these products' }} has changed
        since the original order. Choose the current size each old size should become. Every player
        with that size moves together; you can still edit single rows on the copied order.
      </p>
    </div>

    <div
      v-for="report in reports"
      :key="`${report.order_item_id}-${report.factory_product_id}`"
      class="rounded-lg border border-line p-3"
      data-test="resolve-obsolete-sizes-product"
    >
      <p class="text-sm font-medium mb-2">
        {{ productLabel(report) }}
      </p>
      <div
        v-for="entry in report.obsolete_sizes"
        :key="entry.size"
        class="flex flex-wrap items-center gap-2 py-1"
      >
        <span class="min-w-[9rem] text-sm">
          <span class="font-medium">{{ entry.size }}</span>
          <span class="text-ink-soft">
            · {{ entry.rows }} {{ entry.rows === 1 ? 'row' : 'rows' }}, qty {{ entry.quantity }}
          </span>
        </span>
        <span class="text-ink-soft" aria-hidden="true">→</span>
        <select
          :value="modelValue[replacementKey(report, entry.size)] ?? ''"
          class="min-w-[10rem] rounded-md border border-line bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          :class="{ 'border-red-500': !modelValue[replacementKey(report, entry.size)] }"
          :aria-label="`Replacement for ${entry.size}`"
          data-test="resolve-obsolete-sizes-select"
          @change="choose(report, entry.size, ($event.target as HTMLSelectElement).value)"
        >
          <option value="" disabled>Choose a size…</option>
          <option v-for="size in report.valid_sizes" :key="size" :value="size">
            {{ size }}
          </option>
        </select>
      </div>
    </div>

    <p v-if="remaining > 0" class="text-sm text-red-600" data-test="resolve-obsolete-sizes-hint">
      Choose a replacement for {{ remaining }} more {{ remaining === 1 ? 'size' : 'sizes' }} before
      copying.
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  type ObsoleteSizeReport,
  type SizeReplacementChoices,
  replacementKey,
  unresolvedCount,
} from '@/utils/obsoleteSizes';
import { computed } from 'vue';

const props = defineProps<{
  reports: ObsoleteSizeReport[];
  modelValue: SizeReplacementChoices;
}>();

const emit = defineEmits<{ 'update:modelValue': [value: SizeReplacementChoices] }>();

const remaining = computed(() => unresolvedCount(props.reports, props.modelValue));

function choose(report: ObsoleteSizeReport, size: string, value: string): void {
  emit('update:modelValue', { ...props.modelValue, [replacementKey(report, size)]: value });
}

function productLabel(report: ObsoleteSizeReport): string {
  return (
    report.product_name || (report.sku_number ? `SKU ${report.sku_number}` : 'Unnamed product')
  );
}
</script>
